/**
 * Every call to the server goes through here.
 *
 * `credentials: 'same-origin'` matters: the session is an httpOnly cookie,
 * so the browser must be told to send it. Without this every request after
 * login is anonymous and nothing works, with no obvious cause.
 */

/**
 * The server's password length rule, restated so a short one is refused
 * without a round trip. It is enforced in `server/src/auth.ts`, which the
 * client cannot import; the hint spells the number out and must be
 * changed with it.
 */
export const MIN_PASSWORD_LENGTH = 8;

export const PASSWORD_HINT = 'Eight characters or more.';

export class ApiError extends Error {
  /** The whole answer, for the routes that say more than `error`: a
   * refused sign in carries which address is waiting on its link. */
  constructor(
    public status: number,
    message: string,
    public body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  const text = await response.text();
  const body = text ? JSON.parse(text) : {};

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body.error ?? `request failed (${response.status})`,
      body,
    );
  }
  return body as T;
}

export const get = <T>(path: string) => request<T>(path);
export const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });
export const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

// ---------- shapes the server actually returns ----------

export interface Me {
  userId: number;
  username: string;
  /** Site administrator. Today that means one thing: this account may
   * delete any game, not only its own. */
  isAdmin?: boolean;
  /** Mailed when a turn-mode game is waiting on this player. */
  turnMail?: boolean;
}

/** One seat at a table, running or finished. */
export interface Seat {
  userId: number;
  name: string;
  seat: number;
}

export interface GameSummary {
  id: number;
  status: 'lobby' | 'active' | 'finished' | 'abandoned';
  turn_number: number;
  phase_kind: string | null;
  course_name: string;
  players: number;
  /** Who created the game and therefore advances it. */
  host_user_id: number;
  /** Null if the host has since left the game. */
  host_name: string | null;
  /** Everyone at the table, in seat order. */
  players_list: Seat[];
  updated_at: string;
  /** When the game was opened. */
  created_at: string;
  /** When the turn on show was dealt, which is when the last turn was
   * taken. Null for a game that has not started. */
  last_turn_at: string | null;
  /**
   * What a running game is waiting on: the players who still owe an
   * answer, or the host to advance it.
   *
   * Null for a game still in the lobby, which is waiting on whoever has
   * not joined it rather than on anybody in particular.
   */
  waiting_on:
    | { kind: 'players'; names: string[] }
    | { kind: 'host' }
    | null;

  // ---- what the host set the game up with ----
  /** Seats the host will allow, which is what "N of 8" counts against. */
  max_players: number;
  /** 1 or 0: these come straight off the games row. */
  options_enabled: number;
  debris_enabled: number;
  /** 1 is turn mode, 0 normal mode. */
  turn_mode: number;
  late_join: number;
  /** How many Option cards are in the deck. Null is the whole catalog. */
  option_count: number | null;
  /** Options dealt to every robot at the start. 0 is the ordinary game. */
  starting_options: number;
  /** Null means no programming clock at all. */
  timer_seconds: number | null;
  /** Whether those seconds are the whole table's or the last player's. */
  timer_mode: 'hand' | 'last';
}

/** How the host set a game up, from `/api/games/:id/settings`. */
export interface GameSettings {
  maxPlayers: number;
  optionsEnabled: boolean;
  /** Option cards in the deck. Null is every card of the mode. */
  optionCount: number | null;
  /** How many cards the mode has, for "12 of 30". */
  optionCatalog: number;
  startingOptions: number;
  debrisEnabled: boolean;
  turnMode: boolean;
  /** Null means no programming clock. */
  timerSeconds: number | null;
  timerMode: 'hand' | 'last';
  lateJoin: boolean;
  /** 0 is life tokens off: unlimited lives. */
  lifeTokens: number;
}

export interface PlayerRow {
  robot_id: string;
  seat: number;
  display_name: string;
  colour: string | null;
  user_id: number;
  created_by: number | null;
  status: GameSummary['status'];
  /** Whether the host left the door open once the game is under way.
   * 1 or 0: it comes straight off the games row. */
  late_join: number;
  /** The seat limit the host set. 1 is a solo game. */
  max_players: number;
}

export interface ProgramCard {
  type: 'UTurn' | 'RotateLeft' | 'RotateRight' | 'BackUp' | 'Move1' | 'Move2' | 'Move3';
  priority: number;
}

export interface RobotView {
  id: string;
  x: number;
  y: number;
  facing: 'N' | 'E' | 'S' | 'W';
  damage: number;
  destroyed: boolean;
  eliminated?: boolean;
  virtual?: boolean;
  poweredDown?: boolean;
  /**
   * This robot will begin next turn powered down.
   *
   * Public, but only once programming has closed: it is an ANNOUNCEMENT,
   * and the table plans around it. Absent for everyone but its own player
   * until then, since it is chosen on the programming screen alongside
   * the cards. See `redactRobot` in the server's `view.ts`.
   */
  announcedPowerDownNextTurn?: boolean;
  lives?: number;
  lastTouchedFlag?: number;
  /** The turn this robot joined a game already in progress. It buys no
   * grace of any kind; what it is read for is the starting facing -- the
   * offer turn 1 makes everybody, on the turn that is turn 1 for them. */
  joinedTurn?: number;
  /** Where this robot respawns. Public: archive markers sit on the table
   * in the board game, so everyone can see everyone's. */
  archiveMarker?: { x: number; y: number };
  /** The flag the marker sits on, where it sits on one. The archive is
   * the flag, so the marker is drawn wherever the belts have taken it --
   * see `archiveSquare`. */
  archiveFlag?: number;
  deaths?: number;
  options?: OptionCardView[];
  /** Turn-programmed Options this robot has switched on for the turn.
   * Public, and marked "On" on the chip once the registers are running. */
  activatedOptions?: string[];
  /** Which way this robot's aimed Options are turned for the turn, held
   * back until the registers start for the same reason. An Option with no
   * entry faces front. */
  aimedOptions?: { optionId: string; facing: RelativeFacing }[];
  hand?: ProgramCard[];
  handCount: number;
  registers: (RegisterSlot | null)[];
  registersRevealed: boolean[];
  lockedRegisters?: boolean[];
  /** True once this robot's Abort Switch has been thrown this turn. It
   * cannot be thrown twice, so the button must not be offered again. */
  abortSwitchActive?: boolean;
  /** True once this robot has spent its Recompile this turn. Once per
   * turn, so the button must not be offered again. */
  recompiledThisTurn?: boolean;
}

export type GamePhase =
  | { kind: 'deal' }
  | {
    kind: 'awaitingProgram';
    robotIds: string[];
    /** Robots that have not submitted a program yet. Server-computed:
     * the submissions sit in `pending_submissions` until the last one
     * lands, so `robotIds` does not narrow as they arrive. */
    waitingFor?: string[];
  }
  | { kind: 'awaitingPowerDown'; robotIds: string[] }
  | { kind: 'runningRegister'; register: number }
  | { kind: 'awaitingOptionUse'; register: number; robotIds: string[] }
  | {
      kind: 'awaitingWeaponChoice'; register: number; robotIds: string[];
      /** Which weapons each robot can actually fire, by robot id. */
      usable: Record<string, string[]>;
      /** Who each Fire Control shooter's main laser would hit, in beam
       * order. Only for a robot holding the card. */
      fireControl?: Record<string, string[]>;
    }
  | { kind: 'awaitingAbortChoice'; register: number; robotIds: string[] }
  | {
    kind: 'awaitingRegisterChoices';
    register: number;
    chopShop: string[];
    /** Robots on a chop shop that have not submitted a choice yet.
     * Server-computed: their submissions sit in `pending_choices`, which
     * the engine cannot see. */
    waitingFor?: string[];
  }
  | {
      kind: 'awaitingEndOfTurn';
      facing: string[];
      repair: string[];
      continuePowerDown: string[];
      returnPowerDown: string[];
      /** Robots that still owe a choice. Server-computed: submissions sit
       * in `pending_choices`, which the engine cannot see. */
      waitingFor?: string[];
      /** Destroyed robots holding more than one Option, who choose which
       * to give up. */
      discardOption: string[];
    }
  | { kind: 'gameOver'; winnerId: string | null; reason: 'flag' | 'allEliminated' };

/**
 * A flag, where it is now rather than where the course printed it.
 *
 * A belt carries a flag as it carries anything else lying on it. One
 * marked `lost` is off the board -- in a pit, over the edge, through a
 * trap door or under a crusher -- until the next register puts it back at
 * its printed square, and is drawn nowhere in the meantime.
 */
export interface FlagView {
  number: number;
  isFinal: boolean;
  x: number;
  y: number;
  lost?: 'pit' | 'offCourse' | 'trapDoor' | 'crusher';
}

/** A token on the board: something dropped rather than printed. */
export interface TokenView {
  id: string;
  kind: string;
  x: number;
  y: number;
  facing?: 'N' | 'E' | 'S' | 'W';
  /** Squares of struggling left in a patch of goo. Goo only. */
  remaining?: number;
}

export interface GameView {
  /** When the current phase runs out, ISO, or null when untimed. */
  deadline: string | null;
  /** The host has stopped the clock. `deadline` is null while it is, the
   * same as an untimed game, so this is what separates the two. */
  timerPaused: boolean;
  /**
   * Where every flag is, printed squares included.
   *
   * A flag marked `lost` is off the board until the next register begins.
   * Optional only for a view from a server that predates this being sent,
   * where the layout's own flags are what to draw.
   */
  flags?: FlagView[];
  turnNumber: number;
  phase: GamePhase;
  robots: RobotView[];
  /** Things dropped on the board. Public information. */
  tokens: TokenView[];
  deck: { drawCount: number; discardCount: number };
  /** Option cards a draw could produce: the deck plus the discard, which
   * is shuffled back in when the deck runs dry. Zero when Options are off
   * for this game. */
  optionsAvailable: number;
  /** Whether debris is in play this game. */
  debris: boolean;
  /** Turn mode: the turn is replayed from the log, programming waits for
   * it, one weapon is offered, and Help shows the turn-mode sequence. */
  turnMode: boolean;
  youAre: string | null;
  version: number;
}

export interface CourseLayout {
  courseId: number;
  name: string;
  revision: string;
  width: number;
  height: number;
  boards: {
    id: string; gridX: number; gridY: number; rotation: number;
    cellsWide: number; cellsHigh: number; image: string;
  }[];
  flags: { number: number; x: number; y: number; isFinal: boolean }[];
  starts: { number: number; x: number; y: number }[];
  /** Which elements this course actually has, for the key under the
   * board. In the server's order, which is grouped by what a player is
   * asking about rather than alphabetical. */
  elements: string[];
}

export interface LogEvent {
  type: string;
  robotId: string;
  count?: number;
  from?: { x: number; y: number };
  to?: { x: number; y: number };
  facing?: 'N' | 'E' | 'S' | 'W';
  /** A side of the ROBOT rather than of the compass: which way an aimed
   * Option was turned on `optionAimed`, and which shield went on
   * `damageShielded`. Not `facing`, which is a compass direction on every
   * event that has one. */
  side?: RelativeFacing;
  cause?: string;
  turn?: 'left' | 'right' | 'about';
  terrain?: string;
  /** What was dropped, for a token event. */
  kind?: string;
  /** A card or option name, where the event names one. */
  name?: string;
  /** The option given up, on an exchange at a chop shop. */
  gaveUp?: string;
  /** Which register, where the event concerns one. */
  register?: number;
  /** Who caused it, where the event has a firer distinct from its
   * victim. Absent on rows written before lasers recorded it. */
  sourceId?: string;
  /** Which gun drew the line, where the picture of it differs from the
   * ordinary one. Absent on rows written before it was recorded, and on
   * every shot whose art is the plain one. */
  weapon?: 'doubleLaser';
  /** How many times a robot has been destroyed, on a return to play. */
  deaths?: number;
  /** Whether the flag an archive happened on also counted. Every flag is
   * a repair site, so a robot archives on one either way. Absent on rows
   * logged before it was recorded. */
  tagged?: boolean;
  /** Where a missile moves in the turn order, on the same scale as a
   * card's priority. Missiles fly between the cards, so the log heads
   * their lines with the number the same way it heads a card's. */
  priority?: number;
  /** What a missile went off against. */
  against?: 'robot' | 'wall' | 'repulsor' | 'pusher';
  /** The missile's own token id, so playback can walk the right shell
   * across the board. Absent on rows logged before missiles flew in the
   * replay, which fall back to matching on the square. */
  tokenId?: string;
  /** The robot a missile went off on. Not `sourceId`: on these events the
   * robot the line is filed under is the one that FIRED, so the victim
   * needs a field of its own. */
  targetId?: string;
  card?: ProgramCard;
  /** The cards a randomizer, abort or scramble took OUT of the register,
   * where `card` is what went in. A list, because a register can hold
   * more than one card and all of them go. Absent on rows logged before
   * it was recorded; empty where the register was already bare. */
  replaced?: ProgramCard[];
  /** The two shoves of a robot forced on two axes, first first, with the
   * shooter and priority that put each one there. */
  order?: { facing: 'N' | 'E' | 'S' | 'W'; sourceId: string; priority: number }[];
}

/** One line of table talk. `to` is null when it was said to everyone. */
export interface ChatMessage {
  id: number;
  from: string;
  fromName: string;
  to: string | null;
  text: string;
  at: string;
}

export interface LogEntry {
  seq: number;
  turn: number | null;
  register: number | null;
  action: string;
  phase: string | null;
  at: string;
  events: LogEvent[];
  /** The board before this action. Null on entries logged before frames
   * were recorded at all. */
  frame: LogFrame | null;
}

/** An Option in a frame: what a replay may say about one. */
export interface FrameOption {
  id: string;
  name: string;
  /** Shots left, on an Option that carries a payload. */
  remaining?: number;
  /** A program card is face down on it. Never the card itself: the frame
   * is a public feed and that card has not been seen. */
  holdsCard?: true;
}

export interface FrameRobot {
  id: string; x: number; y: number;
  facing: 'N' | 'E' | 'S' | 'W';
  /** Absent on rows logged before damage was recorded in the frame. */
  damage?: number;
  /** Absent on rows logged before the whole board was recorded -- see
   * `LogFrame`. Those frames carry position and damage and no more. */
  poweredDown?: boolean;
  virtual?: boolean;
  eliminated?: boolean;
  archiveMarker?: { x: number; y: number };
  /** The flag the marker was on, where it was on one, so a replay can draw
   * it riding the belt with that flag rather than jumping a beat later. */
  archiveFlag?: number;
  /** Life tokens left. Absent in a game playing without them, and on
   * frames written before they were recorded. */
  lives?: number;
  /** The Options held at this frame. Absent on a frame written before they
   * were recorded, which is not the same as a robot holding none -- the
   * replay shows the figure only where the frame says. */
  options?: FrameOption[];
  /**
   * The rest of the robot as a spectator saw it, which is what a frame is
   * built from: registers revealed so far, locks, Options switched on and
   * aimed once the registers started, flags touched, deaths, an announced
   * power down. Absent on frames written before frames were built that
   * way, and the robot panels then show the live view.
   */
  registers?: RobotView['registers'];
  registersRevealed?: RobotView['registersRevealed'];
  lockedRegisters?: RobotView['lockedRegisters'];
  activatedOptions?: RobotView['activatedOptions'];
  aimedOptions?: RobotView['aimedOptions'];
  announcedPowerDownNextTurn?: boolean;
  lastTouchedFlag?: number;
  deaths?: number;
}

/**
 * A frame in either of the two shapes the log holds.
 *
 * A BARE ARRAY is a frame from before the token layer was recorded:
 * robots only, and nothing about what was lying on the board. Those
 * cannot be repaired -- debris was laid without an event saying so, so
 * there is nothing to rebuild it from -- and a game made of them can be
 * replayed only as robots on an empty course.
 *
 * The OBJECT carries the board as it stood: the robots, with everything
 * drawn on one, the tokens under them, and the flags where the belts had
 * carried them to. `flags` is absent on a course where none has ever
 * moved, and on frames written before flags were recorded; the course's
 * own layout is what to draw for both.
 */
export type LogFrame =
  | FrameRobot[]
  | { robots: FrameRobot[]; tokens: TokenView[]; flags?: FlagView[] };

/** The robots in a frame, whichever shape it is. */
export function frameRobots(frame: LogFrame): FrameRobot[] {
  return Array.isArray(frame) ? frame : frame.robots;
}

/**
 * The token layer in a frame, or NULL where the frame predates it.
 *
 * Null and empty are different answers and the caller has to tell them
 * apart: empty is a board with nothing on it, null is a board nobody
 * wrote down.
 */
export function frameTokens(frame: LogFrame): TokenView[] | null {
  return Array.isArray(frame) ? null : frame.tokens;
}

/**
 * The flags in a frame, or NULL where the frame does not say.
 *
 * Null covers both a frame written before flags were recorded and a
 * course on which no belt has ever moved one, and the answer to both is
 * the same: draw the flags the course layout carries.
 */
export function frameFlags(frame: LogFrame): FlagView[] | null {
  return Array.isArray(frame) ? null : frame.flags ?? null;
}

export interface BoardSummary {
  id: string;
  width: number;
  height: number;
  image: string;
  /** The expansion this board was published in. Absent for the base
   * game, which the picker shows and sorts as a dash. */
  expansion?: string;
}

export interface PlacedBoardConfig {
  id: string;
  /**
   * The absolute CELL of this piece's top-left corner, NOT a lattice slot.
   * A second 12x12 board along the top row is at `gridX: 12`.
   *
   * This changed when docking bays arrived: a dock is 12x4, and a grid of
   * board-sized slots cannot address one. How coarsely the editor lets a
   * host place a piece is now the editor's business alone.
   */
  gridX: number;
  gridY: number;
  rotation: 0 | 90 | 180 | 270;
}

export interface CourseConfigInput {
  name: string;
  boards: PlacedBoardConfig[];
  dock: PlacedBoardConfig | null;
  flags: { number: number; x: number; y: number }[];
  /** 0 switches life tokens off. */
  lifeTokens: number;
}

export interface CreateGameInput {
  course: CourseConfigInput;
  timerSeconds: number | null;
  /**
   * WHEN those seconds start.
   *
   * `hand` arms the clock as programming opens, so it is how long the
   * whole table has. `last` waits until everyone but one has submitted
   * and gives that player the time, which hurries a table waiting on
   * one person and nobody else. Ignored when there is no timer.
   */
  timerMode: 'hand' | 'last';
  maxPlayers: number;
  /** Options are in play unless the host turns them off. */
  optionsEnabled: boolean;
  /** Debris is in play unless the host turns it off. */
  debrisEnabled: boolean;
  /** Turn mode: every decision made while programming, and no timer.
   * Normal mode unless sent. */
  turnMode?: boolean;
  /** Whether someone may take a seat after the game has started.
   * Open unless the host shuts it. */
  lateJoin: boolean;
  /** Which Option cards are in the deck. Omitted means every card of the
   * mode. */
  optionIds?: string[];
  /** How many Options each robot is dealt at the start, off the deck.
   * 0 is the ordinary game. */
  startingOptions?: number;
}

/**
 * One register as the server sends it.
 *
 * A list, because Options can put more than one thing in a register — two
 * program cards from one, a gadget from another. The FIRST card decides
 * the register's priority.
 */
export interface RegisterSlot {
  /** Cards in this register, PRIMARY FIRST. */
  cards: ProgramCard[];
  gadgets?: string[];
}

/**
 * The card that represents a register: its primary.
 *
 * Decides when the register runs, and is the one card taken when only one
 * can be. Always first in `cards`.
 */
export function slotCard(slot: RegisterSlot | null | undefined): ProgramCard | null {
  return slot?.cards[0] ?? null;
}

export interface OptionCardView {
  id: string;
  name: string;
  text: string;
  timing: 'runTime' | 'mandatory' | 'mainLaser' | 'additionalWeapon'
    | 'optionalWeapon' | 'phaseProgrammed' | 'turnProgrammed';
  effect: string;
  /** The card's full payload, where it has one. */
  payload?: number;
  /** What is left of it. */
  remaining?: number;
  /**
   * What the payload counts. 'ammo' is spent and can be reloaded at a
   * chop shop, and an ammo option can be programmed into as many
   * registers in a turn as it has shots left.
   */
  payloadKind?: 'expends' | 'ammo';
  /** A program card set aside on this Option. Own robot only: it is face
   * down, and the server sends `holdsCard` instead for everyone else. */
  heldCard?: ProgramCard;
  /** Someone else's Option has a card face down on it. That there is one
   * is public; what it is is not. */
  holdsCard?: true;
  /** The card is turned rather than switched on: the player programs
   * which of the robot's four sides it covers. */
  directional?: true;
  /** Holding it can pause a register to ask its player something. Sent
   * with the catalog only. */
  interrupts?: true;
  /** Dealt in a turn-mode game only. Sent with the catalog only. */
  turnMode?: true;
}

/**
 * A side of a robot, named relative to the way it is facing. What an
 * aimed Option is turned to.
 */
export type RelativeFacing = 'front' | 'back' | 'left' | 'right';

/**
 * One finished GAME, not one recorded result: the list this comes from is
 * every `games` row with `status = 'finished'`, so a game abandoned with no
 * winner is in it too, and a deleted game is not.
 */
export interface GameResult {
  game_id: number;
  course_name: string;
  /**
   * Whether a `game_results` row backs this game -- true when it was played
   * to a finish, false when the host abandoned it.
   *
   * The winner fields are meaningless unless this is true: a null winner on
   * a recorded game is a DRAW, a null winner on an unrecorded one only
   * means nobody ever won it.
   */
  recorded: boolean;
  /**
   * Whether this game can be watched back.
   *
   * False for games played before the board was written into the frames,
   * which cannot be replayed at all: the robots were recorded and the
   * debris under them was not. Read off the first frame's shape, which
   * answers for the whole game -- the replay page checks every frame
   * before it plays anything.
   */
  replayable: boolean;
  /** Boards the course was laid out from, the dock included. 0 for games
   * that finished before this was recorded, and for abandoned games. */
  board_count: number;
  /** How many flags the course was played to. 0 on rows written before
   * this was recorded, and for abandoned games. */
  flag_count: number;
  /** Who hosted, from `games.created_by`. Null when that account has been
   * deleted, and the name is null when the host had already left the game. */
  host_user_id: number | null;
  host_name: string | null;
  /** Everyone who played, in seat order. */
  players_list: Seat[];
  winner_user_id: number | null;
  winner_name: string | null;
  player_count: number;
  turns: number;
  /**
   * When play began: the first action, which is the Deal the host's Start
   * runs. NOT when the game was opened -- a table can sit in a lobby for
   * days. Null for a game that was finished without ever being played.
   */
  started_at: string | null;
  finished_at: string;
}

export interface Standing {
  userId: number;
  name: string;
  plays: number;
  wins: number;
  lastWin: string | null;
}

export const api = {
  me: () => get<Me>('/api/me'),
  setTurnMail: (on: boolean) =>
    post<{ ok: true; turnMail: boolean }>('/api/auth/turn-mail', { on }),
  login: (username: string, password: string) =>
    post<{ ok: true }>('/api/auth/login', { username, password }),
  /**
   * Makes the account. `verified` false means a confirmation link is on
   * its way and the account cannot sign in until it is opened; true means
   * this site sends no mail and there is nothing to wait for.
   */
  register: (username: string, password: string, email: string) =>
    post<{ ok: true; verified: boolean }>('/api/auth/register', { username, password, email }),
  /** Spends a confirmation link. Does not sign anyone in. */
  verifyEmail: (token: string) =>
    post<{ ok: true; username: string; already?: boolean }>('/api/auth/verify', { token }),
  /** Another copy of a confirmation link. Always answers the same way. */
  resendVerification: (email: string) =>
    post<{ ok: true }>('/api/auth/verify/resend', { email }),
  logout: () => post<{ ok: true }>('/api/auth/logout'),
  forgotPassword: (email: string) =>
    post<{ ok: true }>('/api/auth/forgot', { email }),
  resetPassword: (token: string, newPassword: string) =>
    post<{ ok: true }>('/api/auth/reset', { token, newPassword }),
  /** `pending` means the address is not on the account yet: a link went to
   * it, and opening that is what moves it. */
  setEmail: (email: string) => post<{ ok: true; pending: boolean }>('/api/auth/email', { email }),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ ok: true }>('/api/auth/password', { currentPassword, newPassword }),
  /** Mailed to the site admin. Fails where the site cannot send mail,
   * rather than taking feedback nobody will read. */
  sendFeedback: (name: string, email: string, message: string) =>
    post<{ ok: true }>('/api/feedback', { name, email, message }),

  games: () => get<{ games: GameSummary[] }>('/api/games'),
  results: () => get<{
    recent: GameResult[];
    standings: Standing[];
    /** Games each board has been played in, most first. A board never
     * used is not listed; `expansion` is absent for the base game. */
    boardUse: { board: string; expansion?: string; games: number }[];
    totals: { games: number; draws: number };
  }>('/api/results'),
  boards: () => get<{ boards: BoardSummary[] }>('/api/boards'),
  optionCatalog: () => get<{ options: OptionCardView[] }>('/api/options'),
  createGame: (input: CreateGameInput) => post<{ gameId: number }>('/api/games', input),
  joinGame: (gameId: number, displayName?: string) =>
    post<{ seat: number; robotId: string }>(`/api/games/${gameId}/join`, { displayName }),
  /** This player's program, sent and waiting for the rest of the table,
   * or null when there is none. Only ever the caller's own. */
  myProgram: (gameId: number) => get<{
    program: {
      registers: (ProgramCard | null)[];
      facing?: 'N' | 'E' | 'S' | 'W';
      setAside?: { optionId: string; card: ProgramCard }[];
      gadgets?: string[][];
      activated?: string[];
      aimed?: { optionId: string; facing: RelativeFacing }[];
      powerDown: boolean;
    } | null;
  }>(`/api/games/${gameId}/my-program`),
  gameSettings: (gameId: number) => get<GameSettings>(`/api/games/${gameId}/settings`),
  players: (gameId: number) => get<{ players: PlayerRow[]; courseName: string | null }>(
    `/api/games/${gameId}/players`,
  ),
  startGame: (gameId: number) => post<{ ok: true }>(`/api/games/${gameId}/start`),
  deleteGame: (gameId: number) => del<{ deleted: true }>(`/api/games/${gameId}`),
  /** Ends a game with no winner. Nothing is written to the rankings. */
  finishGame: (gameId: number) =>
    post<{ finished: true }>(`/api/games/${gameId}/finish`, {}),

  gameView: (gameId: number) => get<GameView>(`/api/games/${gameId}`),
  chat: (gameId: number) => get<{ messages: ChatMessage[] }>(`/api/games/${gameId}/chat`),
  /** `to` is a robot id, or null for everyone at the table. */
  sendChat: (gameId: number, to: string | null, text: string) =>
    post<{ message: ChatMessage }>(`/api/games/${gameId}/chat`, { to, text }),
  courseLayout: (gameId: number) => get<CourseLayout>(`/api/games/${gameId}/course`),
  program: (
    gameId: number,
    registers: (ProgramCard | null)[],
    powerDown = false,
    facing?: 'N' | 'E' | 'S' | 'W',
    /** Options travelling with the submission: a card set aside, and
     * gadgets programmed into registers. */
    extras?: {
      setAside?: { optionId: string; card: ProgramCard }[] | null;
      gadgets?: string[][];
      /** Turn-programmed Options switched on for this turn, by id. */
      activated?: string[];
      /** Which way each aimed Option is turned for this turn. One left
       * out faces front, so only the ones the player moved need sending. */
      aimed?: { optionId: string; facing: RelativeFacing }[];
    }
  ) =>
    post<{
      accepted: boolean;
      waitingFor?: string[];
      advanced?: boolean;
      rejected?: { robotId: string; problems: string[] }[];
    }>(
      `/api/games/${gameId}/program`,
      {
        submission: {
          registers,
          ...(facing ? { facing } : {}),
          ...(extras?.setAside?.length ? { setAside: extras.setAside } : {}),
          ...(extras?.gadgets ? { gadgets: extras.gadgets } : {}),
          ...(extras?.activated?.length ? { activated: extras.activated } : {}),
          ...(extras?.aimed?.length ? { aimed: extras.aimed } : {}),
        },
        powerDown,
      },
    ),
  /** Takes a submitted program back. The cards stay where they are: the
   * server keeps them as a draft, so running out of time after taking one
   * back still plays what was there. Refused once the turn has started. */
  unsubmitProgram: (gameId: number) =>
    del<{ accepted: boolean }>(`/api/games/${gameId}/program`),
  /**
   * Parks a half-finished program, so an expired timer keeps the cards
   * already placed and fills only the gaps around them.
   *
   * Not a submission: it does not count towards starting the turn, and
   * the player is still free to change everything or send nothing.
   */
  programDraft: (
    gameId: number,
    registers: (ProgramCard | null)[],
    /** Announced power down. Carried with the draft because a player who
     * set the toggle has answered that question, whether or not they went
     * on to finish programming. */
    powerDown = false,
    extras?: {
      setAside?: { optionId: string; card: ProgramCard }[] | null;
      gadgets?: string[][];
      activated?: string[];
      aimed?: { optionId: string; facing: RelativeFacing }[];
      /**
       * The starting facing, on the turn the player is offered one.
       *
       * Rides with the draft for the same reason the power down does: it
       * is chosen on this screen, and a player who turned their robot and
       * then ran out of time -- or was started without -- had it forgotten
       * and began pointing north.
       */
      facing?: 'N' | 'E' | 'S' | 'W';
    },
  ) =>
    post<{ parked: boolean }>(
      `/api/games/${gameId}/program-draft`,
      {
        submission: {
          registers,
          ...(extras?.setAside?.length ? { setAside: extras.setAside } : {}),
          ...(extras?.gadgets ? { gadgets: extras.gadgets } : {}),
          ...(extras?.activated?.length ? { activated: extras.activated } : {}),
          ...(extras?.aimed?.length ? { aimed: extras.aimed } : {}),
          ...(extras?.facing ? { facing: extras.facing } : {}),
        },
        powerDown,
      },
    ),
  /** Host only: start the turn without everyone in. Registers left empty
   * are filled at random from that player's own hand. */
  forceProgram: (gameId: number) =>
    post<{ accepted: boolean; advanced?: boolean; forced?: string[] }>(
      `/api/games/${gameId}/force-program`, {},
    ),
  /** Host only: remove a player. In lobby the seat is given up; in play
   * the robot is eliminated, as it would be by spending its last life. */
  removePlayer: (gameId: number, userId: number) =>
    del<{ removed: number; robotId: string; eliminated: boolean }>(
      `/api/games/${gameId}/players/${userId}`,
    ),
  /** Host only: get past the power-down announcement. Anyone who parked an
   * answer keeps it; anyone who did not is recorded as not powering down. */
  forcePowerDown: (gameId: number) =>
    post<{ accepted: boolean; advanced?: boolean; forced?: string[] }>(
      `/api/games/${gameId}/force-power-down`, {},
    ),
  /** Host only: stop the programming clock, or start it again with what it
   * had left. */
  setTimerPaused: (gameId: number, paused: boolean) =>
    post<{ paused: boolean }>(`/api/games/${gameId}/timer`, { paused }),
  /**
   * Host only: answer a stalled register for everyone who has not.
   *
   * Covers both kinds of hold a register can put on the table: the
   * mid-register prompts, where every forced answer declines, and the
   * chop-shop pause, where a robot with no answer falls to the engine's
   * default -- an Option at register 5, nothing at 1 to 4.
   */
  forceRegister: (gameId: number) =>
    post<{ accepted: boolean; advanced?: boolean; forced?: string[] }>(
      `/api/games/${gameId}/force-register`, {},
    ),
  /** Host only: resolve the end of the turn without everyone in. Anyone
   * who has not answered falls to the engine's default, which declines
   * every question and keeps a returning robot's facing as it was. */
  forceEndOfTurn: (gameId: number) =>
    post<{ accepted: boolean; advanced?: boolean; forced?: string[] }>(
      `/api/games/${gameId}/force-end-of-turn`, {},
    ),
  /**
   * One page of the log OLDEST FIRST, starting after `since`.
   *
   * What a replay reads, and the only way to reach the start of a long
   * game: the default order is newest first, and `since` only moves
   * forward, so descending pages never walk back past the first one.
   */
  logPage: (gameId: number, since: number, limit: number) =>
    get<{ entries: LogEntry[] }>(
      `/api/games/${gameId}/log?since=${since}&order=asc&limit=${limit}`,
    ),
  action: (gameId: number, kind: string, choices?: unknown, expectedVersion?: number) =>
    post<{ version: number; phase: GamePhase }>(
      `/api/games/${gameId}/action`,
      { kind, choices, expectedVersion },
    ),
};

/** Rows per log request. The server's ceiling. */
const LOG_PAGE = 500;

/**
 * Every log entry after `since`, OLDEST FIRST, read a page at a time.
 * `since` 0 is the whole log.
 */
export async function logSince(gameId: number, since: number): Promise<LogEntry[]> {
  const all: LogEntry[] = [];
  for (;;) {
    const after = all.length > 0 ? all[all.length - 1].seq : since;
    const page = await api.logPage(gameId, after, LOG_PAGE);
    all.push(...page.entries);
    // A short page is the last page. An empty one ends it too, so a
    // server that stops returning rows cannot spin here.
    if (page.entries.length < LOG_PAGE) return all;
  }
}
